import React, { useEffect, useRef, useState } from "react";
import Map, { Marker, Source, Layer } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Volume2, VolumeX, Navigation, X, Crosshair } from "lucide-react";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/** Distance haversine en mètres */
function distM(la1, lo1, la2, lo2) {
  const R = 6371000;
  const dLa = (la2 - la1) * Math.PI / 180;
  const dLo = (lo2 - lo1) * Math.PI / 180;
  const a = Math.sin(dLa/2)**2 + Math.cos(la1*Math.PI/180)*Math.cos(la2*Math.PI/180)*Math.sin(dLo/2)**2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const ARROWS = {
  left: "↰", right: "↱",
  "slight left": "↖", "slight right": "↗",
  "sharp left": "⬅", "sharp right": "➡",
  straight: "↑", uturn: "↩",
  arrive: "🏁", depart: "🏠",
};

function maneuverLabel(maneuver) {
  if (!maneuver) return "Continuer";
  const type = maneuver.type;
  const mod = maneuver.modifier;
  if (type === "depart") return "Départ";
  if (type === "arrive") return "Arrivée";
  if (type === "turn") return mod ? `Tournez à ${mod === "left" ? "gauche" : mod === "right" ? "droite" : mod}` : "Tournez";
  if (type === "merge") return "Insérez-vous";
  if (type === "roundabout") return "Prenez le rond-point";
  if (type === "fork") return mod ? `Restez à ${mod === "left" ? "gauche" : "droite"}` : "Bifurcation";
  if (type === "end of road") return mod ? `À la fin, ${mod === "left" ? "à gauche" : "à droite"}` : "Fin de route";
  if (type === "continue") return "Continuez";
  if (type === "new name") return "Continuez";
  return type;
}

export default function NavigationView({ open, onOpenChange, mission, onArrived, phase = "pickup" }) {
  const mapRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastSpokenStepRef = useRef(-1);
  const [position, setPosition] = useState(null);
  const [route, setRoute] = useState(null); // {coords, steps}
  const [stepIdx, setStepIdx] = useState(0);
  const [voiceOn, setVoiceOn] = useState(true);
  const [followMode, setFollowMode] = useState(true);
  const [error, setError] = useState(null);

  // Phase courante : "pickup" = aller chercher le colis ; "delivery" = livrer
  const isPickup = phase === "pickup";
  const dest = isPickup
    ? { lat: mission.pickup_lat, lng: mission.pickup_lng,
        label: "Récupération", address: mission.pickup_address }
    : { lat: mission.delivery_lat, lng: mission.delivery_lng,
        label: "Livraison",     address: mission.delivery_address };
  const destLetter = isPickup ? "A" : "B";

  // Reset le trajet quand on change de phase (pickup → delivery)
  useEffect(() => {
    setRoute(null);
    setStepIdx(0);
    lastSpokenStepRef.current = -1;
  }, [phase, mission.id]);

  // Charge le trajet routier détaillé une fois qu'on a la position de départ
  useEffect(() => {
    if (!open || !position || !TOKEN) return;
    if (route) return; // déjà calculé
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${position.lng},${position.lat};${dest.lng},${dest.lat}?steps=true&overview=full&geometries=geojson&language=fr&access_token=${TOKEN}`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        const r0 = d?.routes?.[0];
        if (!r0) return;
        const steps = (r0.legs?.[0]?.steps) || [];
        setRoute({ coords: r0.geometry.coordinates, steps, duration: r0.duration, distance: r0.distance });
      })
      .catch((e) => setError(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, position]);

  // Tracking GPS
  useEffect(() => {
    if (!open) return;
    if (!navigator.geolocation) {
      setError("Géolocalisation non supportée par ce navigateur");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const p = {
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          speed: pos.coords.speed, heading: pos.coords.heading,
        };
        setPosition(p);
        // Le partage avec l'expéditeur est géré séparément par le contrôle
        // « Localisation conducteur », après consentement explicite.
      },
      (err) => setError(`GPS: ${err.message}`),
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 }
    );
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    };
  }, [open, mission.id]);

  // Détecte le step courant et déclenche la voix
  useEffect(() => {
    if (!position || !route) return;
    const steps = route.steps;
    let bestIdx = stepIdx;
    let bestDist = Infinity;
    for (let i = stepIdx; i < steps.length; i++) {
      const loc = steps[i].maneuver?.location;
      if (!loc) continue;
      const d = distM(position.lat, position.lng, loc[1], loc[0]);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
      if (d < 30 && i > stepIdx) {
        // On a passé le step précédent → on avance
        setStepIdx(i);
        break;
      }
    }
    // Avance si on est très proche du step courant et qu'on a passé sa location
    if (bestIdx !== stepIdx && bestDist < 50) {
      setStepIdx(bestIdx);
    }
    // Voix : quand on est entre 200m et 30m du prochain step
    const next = steps[stepIdx + 1];
    if (voiceOn && next && next.maneuver?.location && lastSpokenStepRef.current !== stepIdx + 1) {
      const dn = distM(position.lat, position.lng, next.maneuver.location[1], next.maneuver.location[0]);
      if (dn < 200) {
        lastSpokenStepRef.current = stepIdx + 1;
        speak(`Dans ${Math.round(dn)} mètres, ${next.maneuver.instruction || maneuverLabel(next.maneuver)}`);
      }
    }
    // Arrivée
    const distToDest = distM(position.lat, position.lng, dest.lat, dest.lng);
    if (distToDest < 50 && onArrived) onArrived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, route, stepIdx, voiceOn]);

  // Recentrer la carte sur la position
  useEffect(() => {
    if (!followMode || !position || !mapRef.current) return;
    try {
      mapRef.current.easeTo({
        center: [position.lng, position.lat],
        zoom: 16,
        duration: 800,
        bearing: position.heading || 0,
        pitch: 50,
      });
    } catch (e) {}
  }, [position, followMode]);

  const speak = (text) => {
    try {
      if (!("speechSynthesis" in window)) return;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "fr-FR";
      u.rate = 1.0;
      window.speechSynthesis.speak(u);
    } catch (e) {}
  };

  const currentStep = route?.steps?.[stepIdx];
  const nextStep = route?.steps?.[stepIdx + 1];
  const remainingSteps = route?.steps?.slice(stepIdx) || [];
  const remainingDist = remainingSteps.reduce((acc, s) => acc + (s.distance || 0), 0);
  const remainingTime = remainingSteps.reduce((acc, s) => acc + (s.duration || 0), 0);

  const routeGeoJson = route ? {
    type: "Feature", properties: {},
    geometry: { type: "LineString", coordinates: route.coords },
  } : null;

  if (!TOKEN) {
    const destinationUrl = `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}`;
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="navigation-view-fallback">
          <DialogTitle>Navigation vers {dest.label.toLowerCase()}</DialogTitle>
          <div className="space-y-4">
            <div className="rounded-md border border-[#1F4032]/15 bg-[#FBF7F1] p-4">
              <div className="text-xs uppercase tracking-wide text-neutral-500">Destination</div>
              <div className="mt-1 font-medium text-[#15302A]">{dest.address}</div>
              <div className="mt-2 text-xs text-neutral-600">
                {position ? "Votre position GPS est active sur cet appareil." : "Autorisez la localisation pour confirmer votre position."}
              </div>
              {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
            </div>
            <Button asChild className="h-11 w-full gap-2 rounded-md bg-[#1F4032] hover:bg-[#2A5443]">
              <a href={destinationUrl} target="_blank" rel="noreferrer">
                <Navigation className="h-4 w-4" /> Ouvrir l'itinéraire sur Google Maps
              </a>
            </Button>
            <p className="text-xs leading-relaxed text-neutral-500">
              La carte de suivi MML reste disponible dans le détail de la mission. La navigation guidée intégrée nécessite la clé cartographique de production.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-full w-screen h-[100dvh] p-0 m-0 rounded-none border-0" data-testid="navigation-view">
        <DialogTitle className="sr-only">Navigation GPS</DialogTitle>
        <div className="relative w-full h-full bg-[#1F4032]">
          {/* MAP */}
          <div className="absolute inset-0">
            {position ? (
              <Map
                ref={mapRef}
                mapboxAccessToken={TOKEN}
                initialViewState={{ longitude: position.lng, latitude: position.lat, zoom: 16, pitch: 50 }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                style={{ width: "100%", height: "100%" }}
              >
                {routeGeoJson && (
                  <Source id="route" type="geojson" data={routeGeoJson}>
                    <Layer id="route-line" type="line"
                      paint={{ "line-color": "#1F4032", "line-width": 6, "line-opacity": 0.9 }}
                      layout={{ "line-cap": "round", "line-join": "round" }}
                    />
                  </Source>
                )}
                {/* Position transporteur */}
                <Marker longitude={position.lng} latitude={position.lat} anchor="center">
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: "#1F4032", border: "3px solid #FBF7F1",
                    boxShadow: "0 2px 12px rgba(31, 64, 50, 0.6)",
                  }} />
                </Marker>
                {/* Destination */}
                <Marker longitude={dest.lng} latitude={dest.lat} anchor="bottom">
                  <div style={{
                    background: isPickup ? "#F4A261" : "#15302A",
                    border: "2px solid #FBF7F1", borderRadius: "50%",
                    width: 32, height: 32, display: "flex", alignItems: "center",
                    justifyContent: "center", color: "#FBF7F1", fontWeight: 700,
                    transform: "translate(-50%, -100%)",
                  }}>{destLetter}</div>
                </Marker>
              </Map>
            ) : (
              <div className="flex items-center justify-center h-full text-white">
                <div className="text-center">
                  <Crosshair className="w-12 h-12 mx-auto mb-3 animate-pulse" />
                  <div className="font-display font-bold text-xl">Acquisition GPS…</div>
                  <div className="text-sm text-white/70 mt-2">Autorisez la géolocalisation dans votre navigateur</div>
                  {error && <div className="text-xs text-red-300 mt-3">{error}</div>}
                </div>
              </div>
            )}
          </div>

          {/* TOP : prochaine instruction */}
          {currentStep && (
            <div className="absolute top-0 left-0 right-0 bg-[#1F4032] text-white p-4 pt-6 shadow-lg" data-testid="nav-instruction">
              <div className="max-w-3xl mx-auto">
                {/* Badge de phase : Récupération / Livraison */}
                <div className="flex items-center gap-2 mb-3" data-testid="nav-phase-badge">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider ${isPickup ? "bg-[#F4A261] text-[#15302A]" : "bg-emerald-500 text-white"}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {isPickup ? "Phase 1 — Récupération du colis" : "Phase 2 — Livraison"}
                  </span>
                  <span className="text-xs text-white/70 truncate" title={dest.address}>
                    → {dest.address}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-5xl shrink-0">
                    {ARROWS[nextStep?.maneuver?.modifier || nextStep?.maneuver?.type] || "↑"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/60 uppercase tracking-wider">Prochaine instruction</div>
                    <div className="font-display font-bold text-xl truncate">
                      {nextStep ? (nextStep.maneuver?.instruction || maneuverLabel(nextStep.maneuver)) : "Continuez tout droit"}
                    </div>
                    {currentStep && (
                      <div className="text-sm text-white/70 mt-0.5 truncate">
                        Sur <b>{currentStep.name || "la route actuelle"}</b>
                        {currentStep.distance > 0 && <> · dans {Math.round(currentStep.distance)} m</>}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => onOpenChange(false)}
                    className="shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                    aria-label="Fermer"
                    data-testid="nav-close">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* BOTTOM : barre stats + actions */}
          <div className="absolute bottom-0 left-0 right-0 bg-[#FBF7F1] border-t border-[#1F4032]/15 p-4">
            <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
              <div className="flex gap-6">
                <div>
                  <div className="overline">Distance</div>
                  <div className="font-display font-bold text-xl">
                    {remainingDist > 1000 ? `${(remainingDist/1000).toFixed(1)} km` : `${Math.round(remainingDist)} m`}
                  </div>
                </div>
                <div>
                  <div className="overline">Durée</div>
                  <div className="font-display font-bold text-xl">
                    {Math.round(remainingTime / 60)} min
                  </div>
                </div>
                {position?.speed != null && (
                  <div>
                    <div className="overline">Vitesse</div>
                    <div className="font-display font-bold text-xl">
                      {Math.round((position.speed || 0) * 3.6)} km/h
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFollowMode((v) => !v)}
                  className={`gap-1 ${followMode ? "bg-[#1F4032] text-white hover:bg-[#2A5443] hover:text-white" : ""}`}
                  data-testid="nav-follow">
                  <Crosshair className="w-4 h-4" /> Suivi
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVoiceOn((v) => !v)}
                  className="gap-1"
                  data-testid="nav-voice-toggle">
                  {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  Voix
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
